import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';

import { Import, Upload } from 'lucide-react';

import { DownloadProgress, FileType } from '../../../types/file';
import { FileDownloadProgress } from '../../loading/FileDownloadProgress';
import { EXAMPLE_MODELS, SUPPORTED_FORMATS } from '../constants/modelConstants';
import { UploadButton } from '../styles';
import { FileUploadDialogProps } from '../types';
import { getFileIcon } from '../utils/fileHelpers';

export const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
  open,
  onClose,
  onFileSelect,
  onExampleModelSelect,
  uploadError,
  onClearError,
  isMobile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [downloadProgress, setDownloadProgress] = useState<Array<DownloadProgress>>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Clean up progress when modal closes
  useEffect(() => {
    if (!open) {
      setIsDownloading(false);
      setDownloadProgress([]);
    }
  }, [open]);

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleExampleModelSelect = async (url: string, filename: string) => {
    const model = EXAMPLE_MODELS.find((m) => m.filename === filename);
    if (!model) return;

    const fileId = `${filename}-${Date.now()}`;

    // Initialize progress tracking
    setDownloadProgress([
      {
        fileId,
        fileName: filename,
        fileType: FileType.OBJECT_3D,
        totalSize: model.size,
        downloadedSize: 0,
        isComplete: false,
      },
    ]);
    setIsDownloading(true);

    try {
      // Use fetch API for progress tracking
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }

      const contentLength = response.headers.get('content-length');
      const totalSize = contentLength ? parseInt(contentLength, 10) : model.size;

      let downloadedSize = 0;

      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;

        if (!done && result.value) {
          downloadedSize += result.value.length;

          // Update progress
          setDownloadProgress((prev) =>
            prev.map((item) => (item.fileId === fileId ? { ...item, downloadedSize } : item)),
          );
        }
      }

      // Mark as complete
      setDownloadProgress((prev) =>
        prev.map((item) => (item.fileId === fileId ? { ...item, downloadedSize: totalSize, isComplete: true } : item)),
      );

      // Call the original handler
      onExampleModelSelect(url, filename);
    } catch (_error) {
      setIsDownloading(false);
      setDownloadProgress([]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: 'black',
          color: 'white',
          maxWidth: '500px',
          width: '100%',
        },
      }}
    >
      <DialogTitle>Import 3D Models</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" gutterBottom>
            Select 3D model files to upload. Supported formats:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              mt: 2,
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}
          >
            {SUPPORTED_FORMATS.map((format) => (
              <Box
                key={format}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {getFileIcon(format)}
                <Typography>{format}</Typography>
              </Box>
            ))}
          </Box>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(211, 47, 47, 0.1)' }} onClose={onClearError}>
              {uploadError}
            </Alert>
          )}

          <Box
            sx={{
              border: '2px dashed rgba(179, 236, 73, 0.5)',
              borderRadius: 2,
              p: 1,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                borderColor: '#B3EC49',
                bgcolor: 'rgba(179, 236, 73, 0.05)',
              },
            }}
            onClick={triggerFileInput}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              accept=".obj,.glb"
              onChange={onFileSelect}
            />
            <Upload size={48} color="#B3EC49" style={{ margin: '8px auto' }} />
            <Typography variant="body1" gutterBottom>
              Drag and drop files here or click to browse
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Maximum file size: 1GB
            </Typography>
          </Box>

          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#B3EC49' }}>
              Example Models
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import pre-made 3D models to get started quickly
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {EXAMPLE_MODELS.map((model) => (
                <Box
                  key={model.filename}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getFileIcon(model.filename)}
                    <Box>
                      <Typography variant="body1">{model.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {model.description}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Import size={16} />}
                    onClick={() => handleExampleModelSelect(model.url, model.filename)}
                    sx={{
                      color: '#B3EC49',
                      '&:hover': {
                        bgcolor: 'rgba(179, 236, 73, 0.1)',
                      },
                    }}
                    disabled={isDownloading}
                  >
                    Import
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: 'white',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          Cancel
        </Button>
        <UploadButton onClick={triggerFileInput} startIcon={<Upload />} fullWidth={isMobile}>
          Select Files
        </UploadButton>
      </DialogActions>
      {isDownloading && <FileDownloadProgress fileDownloadProgress={downloadProgress} isLoadingFiles={isDownloading} />}
    </Dialog>
  );
};
